import { useTranslation } from 'react-i18next'
import RichText from './RichText'
import { FILL, STROKE, pick } from './documentTones'
import { localeFor } from '../cycleStatus'

const WIDTH = 720
const LEFT = 190
const RIGHT = 20
const ROW = 92
const PLOT = 50

const segments = (points) => {
  const out = []
  let current = []
  points.forEach((p) => {
    if (p.y == null) {
      if (current.length) out.push(current)
      current = []
    } else {
      current.push(p)
    }
  })
  if (current.length) out.push(current)
  return out
}

// VH-228 phase 0: chart_series block — small multiples, one row per series, each on its own scale
// (the order of movement is the message, not the height).
export function DocumentChart({ block }) {
  const { i18n } = useTranslation()
  const labels = block.x_labels ?? []
  const series = block.series ?? []
  const step = labels.length > 1 ? (WIDTH - LEFT - RIGHT) / (labels.length - 1) : 0
  const xAt = (i) => LEFT + i * step
  const height = series.length * ROW + 40
  const fmt = (v) => (v == null ? '—' : v.toLocaleString(localeFor(i18n.language), { maximumFractionDigits: 1 }))

  return (
    <figure className="space-y-2 break-inside-avoid">
      {block.title && <p className="text-xs text-gray-500">{block.title}</p>}
      <div className="overflow-x-auto print:overflow-visible">
        <svg viewBox={`0 0 ${WIDTH} ${height}`} className="w-full min-w-[520px] print:min-w-0" role="img"
          aria-label={series.map((s) => s.label).join(', ')}>
          {block.marker_index != null && labels[block.marker_index] != null && (
            <g>
              <line x1={xAt(block.marker_index)} x2={xAt(block.marker_index)} y1={4} y2={height - 24}
                className="stroke-red-600" strokeWidth="1.2" strokeDasharray="4 4" />
              {block.marker_label && (
                <text x={xAt(block.marker_index) + 5} y={12} className="fill-red-700 text-[11px] font-semibold">{block.marker_label}</text>
              )}
            </g>
          )}
          {series.map((s, row) => {
            const values = s.values ?? []
            const present = values.filter((v) => v != null)
            const min = Math.min(...present)
            const max = Math.max(...present)
            const top = 26 + row * ROW
            const base = top + 22 + PLOT
            const yAt = (v) => (v == null ? null : max === min ? base - PLOT / 2 : base - ((v - min) / (max - min)) * PLOT)
            const points = values.map((v, i) => ({ x: xAt(i), y: yAt(v) }))
            const parts = segments(points)
            const last = [...values].reverse().find((v) => v != null)
            const split = s.label.indexOf(' (')
            const name = split > 0 ? s.label.slice(0, split) : s.label
            const detail = split > 0 ? s.label.slice(split + 1) : null
            return (
              <g key={`${s.label}-${row}`}>
                <text x={LEFT - 12} y={top + 28} textAnchor="end" className="fill-gray-900 text-[12px] font-semibold">{name}</text>
                {detail && <text x={LEFT - 12} y={top + 43} textAnchor="end" className="fill-gray-500 text-[10px]">{detail}</text>}
                <text x={LEFT - 12} y={top + (detail ? 58 : 44)} textAnchor="end" className="fill-gray-500 text-[11px]">
                  {`${fmt(last)}${s.unit ? ` ${s.unit}` : ''}`}
                </text>
                <line x1={LEFT} x2={WIDTH - RIGHT} y1={base} y2={base} className="stroke-gray-200" strokeWidth="1" />
                {parts.length > 1 && (
                  <polyline points={parts.map((p) => p.map((q) => `${q.x},${q.y}`).join(' ')).join(' ')}
                    fill="none" className={pick(STROKE, s.tone)} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.6" />
                )}
                {parts.map((p, i) => (
                  <polyline key={i} points={p.map((q) => `${q.x},${q.y}`).join(' ')} fill="none"
                    className={pick(STROKE, s.tone)} strokeWidth="2.5" strokeLinejoin="round" />
                ))}
                {points.map((p, i) => (p.y == null ? null : (
                  <circle key={i} cx={p.x} cy={p.y} r={i === block.marker_index ? 4 : 2.2} className={pick(FILL, s.tone)}>
                    <title>{`${labels[i]}: ${fmt(values[i])}`}</title>
                  </circle>
                )))}
                {s.note && (
                  <text x={LEFT} y={top + 10} textAnchor="start" className={`${pick(FILL, s.tone)} text-[11px] font-semibold`}>{s.note}</text>
                )}
              </g>
            )
          })}
          {labels.map((label, i) => (
            <text key={`${label}-${i}`} x={xAt(i)} y={height - 8} textAnchor="middle"
              className={`text-[11px] ${i === block.marker_index ? 'fill-red-700 font-semibold' : 'fill-gray-500'}`}>{label}</text>
          ))}
        </svg>
      </div>
      {block.caption && (
        <figcaption className="text-sm text-gray-600 leading-relaxed"><RichText text={block.caption} /></figcaption>
      )}
    </figure>
  )
}

export default DocumentChart
